import { useEffect, useRef } from 'react'
import { travelTransform } from './geometry'

/* =========================================
   SCROLL TRAVEL
   Slides the block along the belt as the user scrolls. Sliding along
   the belt's axis is a pure screen translation, so we just transform
   two nodes — the block group and the slot clip — directly on the DOM
   (no React re-renders), throttled to one update per frame.

   The travel is PROGRESS-based, not speed-based: progress 0 is the
   top of the page, progress 1 is the moment the products section
   ("Four products…") reaches the top of the viewport — at which point
   the block sits exactly at its stop just before the belt's fold.
   Because the mapping is positional, the timing holds on any screen
   size and at any scroll speed. Respects prefers-reduced-motion.
   ========================================= */

/* Progress hits 1 when the products section's top is this many pixels
   from the viewport top. 0 = the block arrives at its stop exactly as
   the section touches the top edge; positive values make it arrive
   earlier, negative later. */
const END_OFFSET = 4

/* ---- Easing: a CSS-style cubic Bezier ----
   Control points (X1,Y1) (X2,Y2), exactly like cubic-bezier() in CSS.
   Gentle start, fastest through the middle, decelerating into the
   stop — reaching 1 exactly at the end, so arrival timing is fixed.
   The shape sets the PEAK speed (the distance and timing are fixed,
   so motion squeezed into the middle moves faster there): pulling the
   control points apart flattens the curve toward linear (slower peak,
   less contrast); pushing X1 right / X2 left sharpens it. The current
   (0.38, 0.05, 0.55, 1) peaks at ~1.7× the average speed. */
const [BX1, BY1, BX2, BY2] = [0.38, 0.05, 0.55, 1]
const bezX = (t: number) =>
  3 * BX1 * t * (1 - t) ** 2 + 3 * BX2 * t * t * (1 - t) + t ** 3
const bezY = (t: number) =>
  3 * BY1 * t * (1 - t) ** 2 + 3 * BY2 * t * t * (1 - t) + t ** 3
const ease = (x: number) => {
  if (x <= 0) return 0
  if (x >= 1) return 1
  // Invert x(t) by bisection (x is monotone in t), then evaluate y.
  let lo = 0
  let hi = 1
  let t = x
  for (let i = 0; i < 24; i++) {
    if (bezX(t) < x) lo = t
    else hi = t
    t = (lo + hi) / 2
  }
  return bezY(t)
}

export function useScrollTravel(maxTravel: number) {
  const blockRef = useRef<SVGGElement | null>(null)
  const clipRef = useRef<SVGPathElement | null>(null)
  /* Current travel (dz along the belt), readable every frame by the
     wave animation so the belt's flat zone tracks the block. */
  const travelRef = useRef(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const section = document.querySelector<HTMLElement>('.products-section')

    let raf = 0
    const apply = () => {
      raf = 0
      // Measured every frame so resizes / late layout shifts are free.
      const end = section
        ? section.getBoundingClientRect().top + window.scrollY - END_OFFSET
        : window.innerHeight
      const p = Math.min(Math.max(window.scrollY / Math.max(end, 1), 0), 1)
      const dz = ease(p) * maxTravel
      travelRef.current = dz
      const t = travelTransform(dz)
      blockRef.current?.setAttribute('transform', t)
      clipRef.current?.setAttribute('transform', t)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }

    apply()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [maxTravel])

  return { blockRef, clipRef, travelRef }
}
